import auth from "../../firebase/auth";
import api from "../../api";
import { createSuccess, createError } from "../../libs/flash-messages";

const LOProperties = ["_id", "address", "contact_info", "display_name", "email", "headshot", "licensed_locations", "licenses", "locations", "manager", "name", "nmls", "simple_nexus", "title", "url"];

const setLenderProperties = (lenders) => {
   // set up a url and primary email on each LO
   const copyLenders = JSON.parse(JSON.stringify(lenders));
   if (copyLenders) {
      return copyLenders.map(lender => {
         if (lender.new_url && lender.new_url.length > 0) {
            lender.url = lender.new_url[0].url;
         }
         if (lender.new_email && lender.new_email.length > 0) {
            const primary = lender.new_email.filter(email => {
               return email.primary;
            }).shift();
            lender.email = primary.email;
         }
         delete lender.new_url;
         delete lender.new_email;
         return lender;
      });
   }
   return [];
}

const state = {
   filters: {
      name: "",
      nmls: "",
      state: "any",
      address: "",
      sort: "a-z"
   },
   loanOfficers: null,
   active: null,
   locations: null
}

const getters = {
   getLoanOfficers(state) {
      return setLenderProperties(state.loanOfficers);
   },
   getFilteredLoanOfficers(state) {
      const filters = state.filters;
      if (state.loanOfficers) {
         const lenders = setLenderProperties(state.loanOfficers);
         return lenders
            .filter(lo => {
               if (filters.name) {
                  return lo.display_name.toLowerCase().includes(filters.name);
               }
               return true;
            })
            .filter(lo => {
               if (filters.nmls) {
                  return lo.nmls.toString().includes(filters.nmls);
               }
               return true;
            })
            .filter(lo => {
               if (filters.state !== "any") {
                  return lo.licenses.reduce((a, b) => {
                     return a || b.short.includes(filters.state)
                  }, false);
               }
               return true;
            })
            .filter(lo => {
               if (lo.address || lo.locations.length > 0 || lo.licensed_locations.length > 0) {
                  return (`${lo.address.street} ${lo.address.city} ${lo.address.state} ${lo.address.zip}`.toLowerCase().includes(filters.address) ||
                     lo.locations.reduce((a, b) => {
                        return a || `${b.address.street} ${b.address.city} ${b.address.state} ${b.address.zip}`.toLowerCase().includes(filters.address);
                     }, false) ||
                     lo.licensed_locations.reduce((a, b) => {
                        return a || `${b.address.street} ${b.address.city} ${b.address.state} ${b.address.zip}`.toLowerCase().includes(filters.address);
                     }, false)
                  );
               } else {
                  return false;
               }
            })
            .sort((a, b) => {
               if (filters.sort === "a-z") {
                  if (a.display_name > b.display_name) {
                     return 1;
                  } else if (a.display_name < b.display_name) {
                     return -1;
                  } else {
                     return 0;
                  }
               } else {
                  if (a.display_name < b.display_name) {
                     return 1;
                  } else if (a.display_name > b.display_name) {
                     return -1;
                  } else {
                     return 0;
                  }
               }
            });
      }
      return [];
   },
   getFilters(state) {
      return state.filters;
   },
   getActive(state) {
      return state.active;
   },
   getLocations(state) {
      return state.locations.sort((a, b) => {
         if (a.address.city > b.address.city) {
            return 1;
         } else if (a.address.city < b.address.city) {
            return -1;
         } else {
            return 0;
         }
      });
   },
   getLicensesData(state) {
      const lenders = setLenderProperties(state.loanOfficers);
      return lenders
         .filter(officer => {
            return officer.status.live;
         })
         .map(officer => {
            return officer.licenses.map(license => {
               return {
                  name: officer.display_name,
                  nmls: officer.nmls,
                  email: officer.email,
                  short: license.short,
                  state: license.state
               }
            });
         })
         .flat()
         .sort((a, b) => {
            if (a.name > b.name) {
               return 1;
            } else if (a.name < b.name) {
               return -1;
            } else {
               return 0;
            }
         });
   }
}

const actions = {
   async startSetLoanOfficers({ commit, rootState }) {
      const token = rootState.user.token;
      await auth.refreshFlow(auth.provider);
      try {
         const officers = await api.axiosGet(`/api/compliance/loan-officers`, token);
         commit("SET_LOAN_OFFICERS", officers);
      } catch (err) {
         return createError("Could not fetch Loan Officers", err)
      }
   },
   startUnsetLoanOfficers({ commit }) {
      commit('SET_LOAN_OFFICERS', null);
   },
   async startSetLocations({ commit, rootState }) {
      const token = rootState.user.token;
      await auth.refreshFlow(auth.provider);
      try {
         const locations = await api.axiosGet(`/api/compliance/locations`, token);
         commit("SET_LOCATIONS", locations);
      } catch (err) {
         return createError("Could not fetch Locations", err);
      }
   },
   startUnsetLocations({ commit }) {
      commit("SET_LOCATIONS", null);
   },
   startSetFilters({ commit }, form) {
      commit('SET_FILTERS', form);
   },
   async startSetActive({ commit, state }, id) {
      const active = state.loanOfficers.filter(lo => {
         return lo._id === id;
      }).shift();
      if (!active) {
         createError("Could not find that Loan Officer", "");
         const err = new Error("not found");
         return Promise.reject(err);
      }
      commit("SET_ACTIVE", active);
      return Promise.resolve("success")
   },
   startUnsetActive({ commit }) {
      commit("SET_ACTIVE", null);
   },
   async startUpdateLoanOfficer({ rootState }, form) {
      const token = rootState.user.token;
      await auth.refreshFlow(auth.provider);
      const id = form._id;
      try {
         const officer = await api.axiosPut(`/api/compliance/loan-officers/${id}`, form, token);
         // send new officer doc to admin panel socket, send only the form (with desired LO properties) to compliance socket
         this._vm.$socket.io.nsps["/"].emit("UPDATE_USER", officer);
         this._vm.$socket.io.nsps["/compliance"].emit("UPDATE_LOAN_OFFICER", officer);
         createSuccess(`Successfully Updated: ${officer.display_name}`);
      } catch (err) {
         return createError("Problem updating", err);
      }
   }
}

const mutations = {
   SET_LOAN_OFFICERS(state, officers) {
      state.loanOfficers = officers;
   },
   SET_FILTERS(state, form) {
      state.filters.name = form.name.toLowerCase();
      state.filters.nmls = form.nmls.toLowerCase();
      state.filters.state = form.state;
      state.filters.address = form.address.toLowerCase();
      state.filters.sort = form.sort;
   },
   SET_ACTIVE(state, active) {
      state.active = active;
   },
   SET_LOCATIONS(state, locations) {
      state.locations = locations;
   },
   SOCKET_COMP_UPDATE_LOAN_OFFICER(state, form) {
      const userFound = state.loanOfficers.findIndex(user => {
         return user._id === form._id;
      });
      if (userFound >= 0) {
         // loop over desired properties and update them on the found LO
         LOProperties.forEach(prop => {
            state.loanOfficers[userFound][prop] = form[prop];
         });
      }
   },
   SOCKET_NORM_UPDATE_USER(state, form) {
      const userFound = state.loanOfficers.findIndex(user => {
         return user._id === form._id;
      });
      if (userFound >= 0) {
         // loop over desired properties and update them on the found LO
         LOProperties.forEach(prop => {
            state.loanOfficers[userFound][prop] = form[prop];
         });
      }
   },
   SOCKET_NORM_UPDATE_USER_ADDRESS(state, update) {
      state.loanOfficers = state.loanOfficers.map(user => {
         if (user.address) {
            if (user.address.street === update.old.street && user.address.city === update.old.city && user.address.state === update.old.state && user.address.zip === update.old.zip) {
               user.address.street = update.new.street;
               user.address.city = update.new.city;
               user.address.state = update.new.state;
               user.address.zip = parseInt(update.new.zip);
               return user;
            }
         }
         return user;
      });
   },
}

export default {
   namespaced: true,
   state,
   getters,
   actions,
   mutations
}